// ─────────────────────────────────────────────────────────────────────────────
// SystemVitalsCard — the seven DGX vitals for the Console left column.
//
// Rexy runs on the DGX Spark (edgexpert-8cb2, GB10 Grace Blackwell). This panel is a
// simple honest reflection of THAT box. It is purely presentational: props in, no
// socket. Real data only — N/A when a source is genuinely unreadable, never faked.
//
// EVERY WINDOW SHOWS THE SAME CORE. Desktop, iOS, tablet, Android and the VPS site all
// render this one server payload; no client samples its own hardware.
//
// ⭐ COLOUR MEANS SOMETHING DIFFERENT PER ROW (owner ruling 2026-07-24). "High = bad"
// is wrong as a blanket rule: peak thinking speed must never render as alarm-red.
// Each row declares its own `tone`, and the bar colour follows that tone:
//   load     — high is load (GPU, memory, storage): cyan → amber → red
//   bad-high — high is genuinely bad (thermal, connection latency): cyan → amber → red
//   good-high— MORE IS BETTER (throughput): never amber, never red
//   info     — informational (power): high under load is normal, not an alarm
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react';
import { REXY_FONT, REXY_CONSOLE } from '../lib/rexyConsole';
import { RexyVitals } from '../hooks/useRexySocket';

const MONO = REXY_FONT;
const GOOD = '#00FF9C'; // same "genuinely good" green the notes/reports panels use

// Bar ceilings — what a full bar means for each row.
const CONNECTION_MAX_MS = 300; // 300ms+ = a bad link
const THERMAL_MAX_C = 85; // GB10 thermal danger ceiling
const THROUGHPUT_REF_TPS = 100; // reference generation speed for a full bar
const POWER_MAX_W = 150; // GB10 draw ceiling for bar scale (informational only)

type Tone = 'load' | 'bad-high' | 'good-high' | 'info';

interface RowDef {
  label: string;
  sub: string;
  tone: Tone;
  /** 0–100 bar fill for this row's raw value. */
  fill: (v: number, vitals: RexyVitals) => number;
  /** Display string — real units, never a fabricated figure. */
  format: (v: number, vitals: RexyVitals) => string;
  /** The raw value this row reads off the payload. */
  read: (vitals: RexyVitals) => number | null;
}

function clamp(n: number): number {
  return Math.max(0, Math.min(100, n));
}

const GIB = 1024 ** 3;
const TB = 1000 ** 4; // decimal TB — how the 4TB DGX SSD is sold/labelled

// The ruled seven, in the ruled order.
const ROWS: RowDef[] = [
  {
    label: 'NEURAL CORE',
    sub: 'GPU UTILISATION',
    tone: 'load',
    read: (v) => v.gpu_util_pct,
    fill: (v) => clamp(v),
    format: (v) => `${Math.round(v)}%`,
  },
  {
    // The GB10 is a UNIFIED-memory part: GPU memory reads N/A through NVML, so this is
    // Linux's real usable pool from /proc/meminfo — never a fabricated 128 GB VRAM figure.
    label: 'MEMORY',
    sub: 'UNIFIED MEMORY',
    tone: 'load',
    read: (v) => v.memory_used_bytes,
    fill: (v, vit) => (vit.memory_total_bytes ? clamp((v / vit.memory_total_bytes) * 100) : 0),
    format: (v, vit) =>
      vit.memory_total_bytes
        ? `${(v / GIB).toFixed(1)} / ${(vit.memory_total_bytes / GIB).toFixed(1)} GiB`
        : `${(v / GIB).toFixed(1)} GiB`,
  },
  {
    // Unified memory is TRANSIENT (what is loaded right now); storage is her ACCUMULATED
    // LIFE — models, OS, the repo, her memory files, Tier 1/Tier 2, notes, reports,
    // dreamstate, and the LoRA batches to come. It only grows. ALL DGX storage
    // deliberately, not an agent-memory subfolder: all storage is the more accurate
    // answer to "how much room does Rexy have to keep growing?".
    label: 'STORAGE',
    sub: 'DGX SSD',
    tone: 'load',
    read: (v) => v.storage_used_bytes,
    fill: (v, vit) => (vit.storage_total_bytes ? clamp((v / vit.storage_total_bytes) * 100) : 0),
    format: (v, vit) =>
      vit.storage_total_bytes
        ? `${(v / TB).toFixed(2)} / ${(vit.storage_total_bytes / TB).toFixed(2)} TB`
        : `${(v / TB).toFixed(2)} TB`,
  },
  {
    // LATENCY + SIGNAL merged. The old SIGNAL was browser→Flask RTT that saturated at
    // 100% under 50ms — a fake constant. This is a real server-sampled round-trip.
    label: 'CONNECTION',
    sub: 'STARLINK RTT',
    tone: 'bad-high',
    read: (v) => v.connection_ms,
    fill: (v) => clamp((v / CONNECTION_MAX_MS) * 100),
    format: (v) => `${v.toFixed(1)}ms`,
  },
  {
    label: 'THERMAL',
    sub: 'GB10 TEMPERATURE',
    tone: 'bad-high',
    read: (v) => v.gpu_temp_c,
    fill: (v) => clamp((v / THERMAL_MAX_C) * 100),
    format: (v) => `${Math.round(v)}°C`,
  },
  {
    // HIGH IS GOOD — how fast she is actually thinking. Idle reads 0 tok/s, which is
    // true about an idle GPU; N/A here would be a lie.
    label: 'THROUGHPUT',
    sub: 'GENERATION SPEED',
    tone: 'good-high',
    read: (v) => v.generation_tokens_per_s,
    fill: (v) => clamp((v / THROUGHPUT_REF_TPS) * 100),
    format: (v) => `${Math.round(v)} tok/s`,
  },
  {
    label: 'POWER',
    sub: 'GPU POWER DRAW',
    tone: 'info',
    read: (v) => v.gpu_power_w,
    fill: (v) => clamp((v / POWER_MAX_W) * 100),
    format: (v) => `${v.toFixed(1)} W`,
  },
];

// The colour rule, per tone — NOT one rule for all rows.
function barColor(tone: Tone, fill: number): string {
  switch (tone) {
    case 'good-high':
      // More is better: brighter as it climbs, never an alarm colour.
      return fill >= 50 ? GOOD : REXY_CONSOLE.primary;
    case 'info':
      // Informational only — a high draw under load is normal, not a fault.
      return REXY_CONSOLE.primary;
    case 'load':
    case 'bad-high':
    default:
      if (fill >= 80) return REXY_CONSOLE.critical;
      if (fill >= 60) return REXY_CONSOLE.warning;
      return REXY_CONSOLE.primary;
  }
}

function VitalRow({ def, vitals }: { def: RowDef; vitals: RexyVitals | null }) {
  const raw = vitals ? def.read(vitals) : null;
  const fill = raw == null || !vitals ? 0 : def.fill(raw, vitals);
  // Start at 0 then transition, so first paint animates in and later values interpolate.
  const [w, setW] = useState(0);
  useEffect(() => {
    setW(fill);
  }, [fill]);
  const color = barColor(def.tone, fill);

  return (
    <div style={styles.row}>
      <div style={styles.rowTop}>
        <span style={styles.label}>{def.label}</span>
        <span style={styles.value}>
          {raw == null || !vitals ? 'N/A' : def.format(raw, vitals)}
        </span>
      </div>
      <div style={styles.track}>
        <div style={{ ...styles.fill, width: `${w}%`, backgroundColor: color }} />
      </div>
      <div style={styles.sub}>{def.sub}</div>
    </div>
  );
}

export interface SystemVitalsCardProps {
  vitals: RexyVitals | null;
}

export default function SystemVitalsCard({ vitals }: SystemVitalsCardProps) {
  return (
    <div style={styles.card}>
      <div style={styles.sectionHeader}>║ SYSTEM VITALS</div>
      {ROWS.map((def) => (
        <VitalRow key={def.label} def={def} vitals={vitals} />
      ))}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    border: `1px solid ${REXY_CONSOLE.cardBorder}`,
    background: REXY_CONSOLE.cardBg,
    borderRadius: 6,
    padding: 14,
    // Dimensional, never flat (CLAUDE.md UI §3): soft drop shadow + faint top-edge highlight.
    boxShadow: '0 4px 14px rgba(0,0,0,0.45), inset 0 1px 0 rgba(0,191,255,0.06)',
    minWidth: 0,
    maxWidth: '100%',
    overflowWrap: 'anywhere',
  },
  sectionHeader: {
    fontWeight: 700,
    fontFamily: MONO,
    fontSize: 12,
    letterSpacing: 2,
    color: REXY_CONSOLE.primary,
    marginBottom: 14,
  },
  row: {
    marginBottom: 12,
  },
  rowTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 10,
    marginBottom: 4,
  },
  label: {
    fontFamily: MONO,
    fontSize: 11,
    letterSpacing: 1.5,
    color: REXY_CONSOLE.primary,
    whiteSpace: 'nowrap',
  },
  value: {
    fontFamily: MONO,
    fontSize: 11,
    color: REXY_CONSOLE.primary,
    textAlign: 'right',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  track: {
    height: 3,
    background: REXY_CONSOLE.panel,
    borderRadius: 2,
    overflow: 'hidden',
  },
  fill: {
    height: 3,
    borderRadius: 2,
    transition: 'width 600ms ease, background-color 300ms linear',
  },
  sub: {
    fontFamily: MONO,
    fontSize: 9,
    letterSpacing: 1,
    color: REXY_CONSOLE.dimText,
    marginTop: 4,
  },
};
